const FX_CACHE_KEY = 'Monolith-fx-rates';
const FX_API_URLS = [
  'https://open.er-api.com/v6/latest/USD',
  'https://latest.currency-api.pages.dev/v1/currencies/usd.json',
];
const TRACKED_CODES = ['USD', 'GBP', 'EUR', 'NGN', 'INR', 'CAD', 'AUD', 'JPY'];

/** Static fallbacks if the API omits a code */
const STATIC_FALLBACKS = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.92,
  NGN: 1550,
  INR: 83,
  CAD: 1.36,
  AUD: 1.52,
  JPY: 150,
};

/** @type {Record<string, number> | null} */
let liveRates = null;
let liveFetchedAt = null;
let liveDate = null;

function todayUtc() {
  return new Date().toISOString().slice(0, 10);
}

export function getLiveExchangeRates() {
  return liveRates;
}

export function getLiveRate(currencyCode) {
  if (!currencyCode) return null;
  if (currencyCode === 'USD') return 1;
  if (!liveRates) return null;
  const rate = liveRates[currencyCode];
  return typeof rate === 'number' && rate > 0 ? rate : null;
}

export function getLiveMeta() {
  return {
    fetchedAt: liveFetchedAt,
    date: liveDate,
    hasLiveRates: Boolean(liveRates),
  };
}

function applyRates(rates, meta = {}) {
  liveRates = rates;
  liveFetchedAt = meta.fetchedAt || Date.now();
  liveDate = meta.date || todayUtc();
}

function readCache() {
  try {
    const raw = localStorage.getItem(FX_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCache(rates, date) {
  try {
    localStorage.setItem(
      FX_CACHE_KEY,
      JSON.stringify({
        date,
        fetchedAt: Date.now(),
        rates,
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

function buildRateMap(apiRates) {
  const map = { USD: 1 };
  TRACKED_CODES.forEach(code => {
    if (code === 'USD') return;
    const value = apiRates?.[code];
    if (typeof value === 'number' && value > 0) {
      map[code] = value;
    } else if (STATIC_FALLBACKS[code] != null) {
      map[code] = STATIC_FALLBACKS[code];
    }
  });
  return map;
}

function normalizeApiPayload(payload, url) {
  // open.er-api.com shape: { result, rates: { GBP: n, ... }, time_last_update_utc }
  if (payload?.rates && typeof payload.rates === 'object') {
    const upper = {};
    Object.entries(payload.rates).forEach(([code, value]) => {
      upper[String(code).toUpperCase()] = Number(value);
    });
    return {
      rates: upper,
      date: payload.time_last_update_utc
        ? new Date(payload.time_last_update_utc).toISOString().slice(0, 10)
        : todayUtc(),
      provider: url.includes('er-api') ? 'open.er-api' : 'rates-object',
    };
  }

  // currency-api.pages.dev shape: { date, usd: { gbp: n, ngn: n, ... } }
  if (payload?.usd && typeof payload.usd === 'object') {
    const upper = {};
    Object.entries(payload.usd).forEach(([code, value]) => {
      upper[String(code).toUpperCase()] = Number(value);
    });
    return {
      rates: upper,
      date: payload.date || todayUtc(),
      provider: 'currency-api',
    };
  }

  throw new Error('Unrecognized FX API payload');
}

async function fetchRatesFromNetwork() {
  let lastError = null;

  for (const url of FX_API_URLS) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`FX API failed (${response.status}) for ${url}`);
      }
      const payload = await response.json();
      if (payload?.result && payload.result !== 'success') {
        throw new Error('FX API returned an unsuccessful result');
      }
      return normalizeApiPayload(payload, url);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('All FX API endpoints failed');
}

/**
 * Load USD→currency rates for today. Uses localStorage day cache, then live APIs.
 * Falls back to null (callers use static table) if the network request fails.
 */
export async function loadExchangeRates({ force = false } = {}) {
  const today = todayUtc();
  const cached = readCache();

  if (!force && cached?.date === today && cached?.rates?.USD) {
    applyRates(cached.rates, { date: cached.date, fetchedAt: cached.fetchedAt });
    return { rates: cached.rates, source: 'cache', date: cached.date };
  }

  const remote = await fetchRatesFromNetwork();
  const rates = buildRateMap(remote.rates || {});
  if (!rates.GBP && !rates.EUR) {
    throw new Error('FX API missing expected currency rates');
  }

  const date = remote.date || today;
  writeCache(rates, date);
  applyRates(rates, { date, fetchedAt: Date.now() });

  return { rates, source: remote.provider || 'network', date };
}
